import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { organizationApi } from '../../../../services/api';
import Modal from '../../../common/Modal';
import BusinessUnitSelector from '../../../common/BusinessUnitSelector';
import { toPickerValue } from '../../../../utils/dateOnly';

/**
 * ADR-118 — Move Business Unit dialog.
 *
 * Scope is the current Cluster by default; an explicit switch widens the
 * picker to every Cluster. The server decides whether the chosen parent
 * makes this a plain re-parent or a cross-cluster transfer (preview endpoint); in the
 * latter case the dialog turns into "transfer mode": impact panel, effective date,
 * confirmation. The dialog never sends a cluster id — only the destination parent.
 */

const SCOPE = Object.freeze({ CURRENT: 'current', ALL: 'all' });
const CONFIRMATION = Object.freeze({ NONE: 'none', CHECKBOX: 'checkbox', TYPED_NAME: 'typed_name' });

const DATE_FORMAT = 'YYYY-MM-DD';
const todayIso = () => dayjs().format(DATE_FORMAT);

const isInHierarchy = (targetId, rootBu) => {
  if (!rootBu) return false;
  if (rootBu.id === targetId) return true;
  if (rootBu.childBus) {
    return rootBu.childBus.some(child => isInHierarchy(targetId, child));
  }
  return false;
};

const MoveBusinessUnitModal = ({ isOpen, businessUnit, clusterId, clusterName, onClose, onMoved }) => {
  const [rules, setRules] = useState(null);
  const [scope, setScope] = useState(SCOPE.CURRENT);
  const [destinationId, setDestinationId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [confirmation, setConfirmation] = useState('');
  const [checked, setChecked] = useState(false);
  const [reason, setReason] = useState('');

  // Reset every time the dialog opens for a (possibly different) unit
  useEffect(() => {
    if (!isOpen) return;
    setScope(SCOPE.CURRENT);
    setDestinationId(null);
    setPreview(null);
    setPreviewError(null);
    setEffectiveDate(todayIso());
    setConfirmation('');
    setChecked(false);
    setReason('');
    organizationApi.getBusinessUnitMoveRules()
      .then(setRules)
      .catch(() => setRules({ crossCluster: {}, caller: { canTransfer: false } }));
  }, [isOpen, businessUnit?.id]);

  // Dry-run whenever the destination changes
  useEffect(() => {
    if (!isOpen || !businessUnit?.id || !destinationId) {
      setPreview(null);
      setPreviewError(null);
      return undefined;
    }
    let cancelled = false;
    setIsPreviewing(true);
    organizationApi.getBusinessUnitMovePreview(businessUnit.id, destinationId)
      .then((result) => { if (!cancelled) { setPreview(result); setPreviewError(null); } })
      .catch((err) => { if (!cancelled) { setPreview(null); setPreviewError(err.message || 'Failed to preview the move'); } })
      .finally(() => { if (!cancelled) setIsPreviewing(false); });
    return () => { cancelled = true; };
  }, [isOpen, businessUnit?.id, destinationId]);

  const callerCanTransfer = Boolean(rules?.caller?.canTransfer);
  const unitTypeCode = (businessUnit?.orgUnitType?.code || '').toLowerCase();
  const allowedTypes = rules?.crossCluster?.allowedOrgUnitTypeCodes || [];
  const unitTypeTransferable = allowedTypes.length === 0 || allowedTypes.includes(unitTypeCode);

  const scopeSwitchReason = useMemo(() => {
    if (!rules) return 'Loading…';
    if (!callerCanTransfer) return 'You are not allowed to move business units between Clusters';
    if (!unitTypeTransferable) return `${businessUnit?.orgUnitType?.name || 'This'} business units stay within their Cluster`;
    return '';
  }, [rules, callerCanTransfer, unitTypeTransferable, businessUnit]);

  const isTransfer = preview?.kind === 'transfer';
  const requirements = preview?.requirements;
  const confirmationMode = requirements?.confirmation || CONFIRMATION.NONE;

  const confirmationSatisfied = !isTransfer
    || confirmationMode === CONFIRMATION.NONE
    || (confirmationMode === CONFIRMATION.CHECKBOX && checked)
    || (confirmationMode === CONFIRMATION.TYPED_NAME && confirmation.trim() === (requirements?.confirmationText || businessUnit?.name));

  const effectiveDateSatisfied = !isTransfer || !requirements?.effectiveDate || Boolean(effectiveDate);

  const canSubmit = Boolean(destinationId)
    && Boolean(preview)
    && !preview.noop
    && !preview.blocked
    && !isPreviewing
    && !isSubmitting
    && confirmationSatisfied
    && effectiveDateSatisfied;

  const disabledItemsCallback = useCallback((bu, allBus) => {
    if (!businessUnit) return null;
    if (bu.id === businessUnit.id) {
      return { disabled: true, reason: 'Current Business Unit' };
    }
    const movingBu = allBus.find(b => b.id === businessUnit.id);
    if (movingBu && isInHierarchy(bu.id, movingBu)) {
      return { disabled: true, reason: 'Child Business Unit' };
    }
    if (bu.id === businessUnit.parentBuId) {
      return { disabled: true, reason: 'Current Parent' };
    }
    return null;
  }, [businessUnit]);

  const handleScopeChange = (_event, next) => {
    if (!next || next === scope) return;
    setScope(next);
    setDestinationId(null);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setIsSubmitting(true);
      const result = await organizationApi.moveBusinessUnit(businessUnit.id, destinationId, {
        effectiveDate: isTransfer ? effectiveDate : undefined,
        confirmation: isTransfer ? (confirmationMode === CONFIRMATION.CHECKBOX ? checked : confirmation) : undefined,
        reason: reason || undefined
      });
      onMoved(result, { kind: preview.kind, destinationName: preview.impact?.destination?.clusterName });
    } catch (err) {
      onMoved(null, { error: err });
    } finally {
      setIsSubmitting(false);
    }
  };

  const destinationClusterName = preview?.impact?.destination?.clusterName;
  const submitText = isTransfer
    ? `Transfer to ${destinationClusterName || 'Cluster'}`
    : 'Move Business Unit';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Move ${businessUnit?.name || ''}`}
      onSubmit={handleSubmit}
      submitText={submitText}
      submitDisabled={!canSubmit}
      cancelText="Cancel"
      width="640px"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Choose the new parent from</Typography>
          <Tooltip title={scopeSwitchReason || ''} disableHoverListener={!scopeSwitchReason}>
            <span>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={scope}
                onChange={handleScopeChange}
                disabled={Boolean(scopeSwitchReason)}
                aria-label="Cluster scope"
              >
                <ToggleButton value={SCOPE.CURRENT} aria-label="current cluster">
                  Current{clusterName ? ` (${clusterName})` : ''}
                </ToggleButton>
                <ToggleButton value={SCOPE.ALL} aria-label="all clusters">
                  All Clusters
                </ToggleButton>
              </ToggleButtonGroup>
            </span>
          </Tooltip>
      </Box>

      <BusinessUnitSelector
        key={scope}
        value={destinationId}
        onChange={setDestinationId}
        label="Select Parent Business Unit"
        clusterId={scope === SCOPE.CURRENT ? clusterId : null}
        placeholder="Select a parent business unit"
        showEmptyOption={false}
        disabledItemsCallback={disabledItemsCallback}
        showStructureType
        fullWidth
      />

      {isPreviewing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Checking the impact of this move…</Typography>
        </Box>
      )}

      {previewError && <Alert severity="error" sx={{ mt: 2 }}>{previewError}</Alert>}

      {preview?.blocked && (
        <Alert severity="error" sx={{ mt: 2 }}>{preview.blocked.message}</Alert>
      )}

      {preview && isTransfer && !preview.blocked && (
        <Box sx={{ mt: 2 }} data-testid="transfer-mode">
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>{businessUnit?.name}</strong> and everything under it will leave{' '}
            <strong>{preview.impact?.source?.clusterName}</strong> and join{' '}
            <strong>{destinationClusterName}</strong>.
          </Alert>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip size="small" label={`${preview.impact.businessUnits} business unit${preview.impact.businessUnits === 1 ? '' : 's'}`} />
            <Chip size="small" label={`${preview.impact.projects} project${preview.impact.projects === 1 ? '' : 's'}`} />
            <Chip size="small" label={`${preview.impact.employees} allocated employee${preview.impact.employees === 1 ? '' : 's'}`} />
            {preview.impact.employeesClusterMismatch > 0 && (
              <Chip size="small" color="warning" variant="outlined" label={`${preview.impact.employeesClusterMismatch} keep their current cluster`} />
            )}
          </Box>

          {preview.impact.warnings?.length > 0 && (
            <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 2 }}>
              {preview.impact.warnings.map(w => (
                <Typography key={w.code} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {w.message}
                </Typography>
              ))}
            </Box>
          )}

          {requirements?.effectiveDate && (
            <DatePicker
              label="Effective date"
              value={toPickerValue(effectiveDate)}
              onChange={(value) => setEffectiveDate(value && value.isValid() ? value.format(DATE_FORMAT) : '')}
              minDate={requirements.maxBackdateDays ? dayjs().subtract(requirements.maxBackdateDays, 'day') : undefined}
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  size: 'small',
                  helperText: requirements.maxBackdateDays ? `May be back-dated up to ${requirements.maxBackdateDays} days` : undefined,
                  sx: { mb: 2, minWidth: 260 }
                }
              }}
            />
          )}

          <TextField
            label="Reason (optional)"
            size="small"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ mb: 2 }}
          />

          {confirmationMode === CONFIRMATION.TYPED_NAME && (
            <TextField
              label={`Type "${requirements.confirmationText || businessUnit?.name}" to confirm`}
              size="small"
              fullWidth
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="off"
            />
          )}

          {confirmationMode === CONFIRMATION.CHECKBOX && (
            <FormControlLabel
              control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
              label="I understand this moves the unit to another Cluster"
            />
          )}
        </Box>
      )}
    </Modal>
  );
};

MoveBusinessUnitModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  businessUnit: PropTypes.object,
  clusterId: PropTypes.string,
  clusterName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  /** (result | null, { kind?, destinationName?, error? }) */
  onMoved: PropTypes.func.isRequired,
};

export default MoveBusinessUnitModal;
